// Proposition TRAME — logique d'affichage de l'écran (fonctions pures, testées : tests/unit/proposition-ecran.test.ts).
// Aucune règle de calcul ici : le moteur et le serveur font foi. L'écran met en forme, colore, localise et explique.
import { aireM2, distance, verifierPolygone } from '@shared/proposition/geometrie.ts';
import {
  GEOMETRIE_ELEMENT, USAGES_PIECE, type CriteresProposition, type PointControle, type Pt,
  type TypeElement, type TypePlafond, type UsagePiece, type VarianteProposition,
} from '@shared/proposition/types.ts';

// Mise en forme commune à l'écran et au PDF du plan annoté (teintes, diamètres, points numérotés, valeurs du comparatif).
export {
  kw, pct, metres, nombre, centre, etatPiece, LIBELLE_TON, COULEURS_DIAMETRE, MOTIFS_DIAMETRE, cleDiametre, stylesDiametres, ancrageTrace,
  positionPoint, pointsLocalises, decalages, montantHt, contientMontant, valeurCritere, MEILLEUR_PETIT, meilleurs, couverturesParPiece,
  type TonPiece, type EtatPiece, type StyleDiametre, type PointLocalise,
} from '@shared/proposition/presentation.ts';

/* ─────────────────────────────── types des réponses de l'API (docs/CONTRAT-API-PROPOSITION.md) ─────────────────────────────── */

export interface PieceVue {
  id: string; planId: string; nom: string; polygone: Pt[]; usage: UsagePiece; traitee: boolean; lieuId: string | null;
  surfaceM2: number | null; surfaceCalculeeM2: number | null; surfaceReleveeM2: number | null; surfaceSource: string | null;
  hauteurSousPlafondM: number | null; hauteurSource: string | null; plafond: TypePlafond | null; fauxPlafondACreer: boolean;
  trame: { moduleMm: number; origine: Pt | null } | null; plenumMm: number | null; plenumSource: string | null;
  occupants: number | null; occupantsSource: string | null; eclairageW: number | null; eclairageSource: string | null;
  equipementsW: number | null; equipementsLatentW: number | null; equipementsSource: string | null;
  airNeufM3h: number | null; airNeufSource: string | null; airNeufParCta: boolean; soufflageT: number | null; soufflageHr: number | null; soufflageSource: string | null;
  acoustiqueDba: number | null; acoustiqueBase: string | null; acoustiqueSource: string | null; mursExterieurs: number[];
  validee: boolean; validation: { par: string; le: string } | null; mention: string | null; demo: boolean; majLe: string;
}
export type PieceSaisie = Omit<PieceVue, 'id' | 'planId' | 'surfaceM2' | 'surfaceCalculeeM2' | 'validee' | 'validation' | 'mention' | 'demo' | 'majLe'>;

export interface ElementVue {
  id: string; planId: string; type: TypeElement; geometrie: Pt[];
  attributs: { plenumMm?: number | null; plenumSource?: string | null; motif?: string | null; support?: string | null; altitudeM?: number | null; altitudeSource?: string | null;
    lienId?: string | null; nature?: string | null; repere?: string | null; traversee?: 'interdite' | 'avec_calfeutrement' | null };
  demo: boolean; majLe: string;
}

export type StatutProposition = 'calculee' | 'retenue' | 'validee' | 'convertie' | 'rejetee' | 'perimee';
/** Critères tels que servis : sans droit aux coûts, `cout.masque` est vrai et aucun montant n'est transmis. */
export type CriteresVus = Omit<CriteresProposition, 'cout'> & { cout: { complet: boolean; totalHt: string | null; manquants: string[]; masque?: boolean } };
export interface LigneVue {
  id: string; runId: string; libelle: string; origine: 'trame' | 'technicien'; strategie: string; rang: number; statut: StatutProposition;
  explication: string; criteres: CriteresVus; motifChoix: string | null; motifRejet: string | null; demo: boolean;
  calculeLe: string | null; retenueLe: string | null; valideeLe: string | null; reprisLe: string | null;
}
export interface CalculVue {
  runId: string; calculeLe: string; versionMoteur: string; empreinte: string; strategies: string[]; demo: boolean; entreesValidees: boolean;
  pointsEntrees: PointControle[]; mentions: string[]; doublons: { strategie: string; identiqueA: string }[]; variantes: LigneVue[];
}
export interface DetailVue extends LigneVue {
  variante: VarianteProposition; mentions: string[]; pointsEntrees: PointControle[]; entreesValidees: boolean; versionMoteur: string; empreinte: string;
  perimee: boolean; levees: Record<string, string> | null;
}
export interface ComparaisonVue {
  runId: string; perimee: boolean; mentions: string[];
  colonnes: { id: string; libelle: string; rang: number; origine: 'trame' | 'technicien'; statut: StatutProposition; explication: string }[];
  criteres: { cle: string; libelle: string; valeurs: unknown[] }[];
  classement: { rang: number; varianteId: string; libelle: string; explication: string }[];
}
export interface RepriseVue { ok: true; statut: 'convertie'; types: number; reperes: number; liaisons: number; lignes: number; fiches: number; avertissements: string[] }

/* ─────────────────────────────── formats ─────────────────────────────── */

export const LIBELLE_USAGE = Object.fromEntries(USAGES_PIECE.map((u) => [u[0], u[1]])) as Record<UsagePiece, string>;
export const LIBELLE_PLAFOND: Record<TypePlafond, string> = {
  faux_plafond_dalles: 'Faux plafond en dalles', faux_plafond_plein: 'Faux plafond plein', plafond_brut: 'Plafond brut (sans faux plafond)', autre: 'Autre',
};
export const LIBELLE_ELEMENT: Record<TypeElement, string> = {
  circulation: 'Circulation (passage des réseaux)', zone_interdite: 'Zone interdite', emplacement_ue: 'Emplacement autorisé du groupe extérieur',
  ue_imposee: 'Groupe extérieur imposé', passage_vertical: 'Passage entre étages', evacuation_condensats: 'Point de rejet des condensats',
  tableau_electrique: 'Tableau électrique', point_gtb: 'Point GTB', percement_interdit: 'Percement interdit (mur)',
};
export const LIBELLE_STATUT: Record<StatutProposition, string> = {
  calculee: 'Calculée', retenue: 'Retenue', validee: 'Validée', convertie: 'Reprise en brouillon', rejetee: 'Rejetée', perimee: 'Périmée',
};
export const consigneGeometrie = (t: TypeElement) => GEOMETRIE_ELEMENT[t] === 'point' ? 'Touchez le plan à l’endroit de l’élément.'
  : GEOMETRIE_ELEMENT[t] === 'polyligne' ? 'Posez les points le long du mur, puis « Terminer ».' : 'Posez les sommets du contour ; touchez le premier sommet pour fermer.';

/* ─────────────────────────────── préparation : données manquantes ─────────────────────────────── */

const fauxPlafond = (p: { plafond: TypePlafond | null }) => p.plafond === 'faux_plafond_dalles' || p.plafond === 'faux_plafond_plein';

/** Champs « à saisir » d'une pièce traitée (clés des champs, pour les mettre en évidence dans la fiche). */
export function champsASaisir(p: Pick<PieceVue, 'usage' | 'traitee' | 'lieuId' | 'hauteurSousPlafondM' | 'plafond' | 'plenumMm' | 'occupants' | 'surfaceM2'>): string[] {
  const l: string[] = [];
  if (!p.lieuId) l.push('lieuId');
  if (p.usage === 'non_traite' || !p.traitee) return l;
  if (p.surfaceM2 == null) l.push('surface');
  if (p.hauteurSousPlafondM == null) l.push('hauteur');
  if (p.plafond == null) l.push('plafond');
  if (fauxPlafond(p) && p.plenumMm == null) l.push('plenum');
  if (p.occupants == null) l.push('occupants');
  return l;
}
const LIBELLE_CHAMP: Record<string, string> = {
  lieuId: 'zone du site à rattacher', surface: 'surface (échelle à valider ou surface relevée)', hauteur: 'hauteur sous plafond', plafond: 'type de plafond',
  plenum: 'hauteur du plénum', occupants: 'occupation',
};

export interface Manque { cle: string; niveau: 'bloquant' | 'a_saisir' | 'a_valider'; message: string; planId: string | null; pieceId?: string; elementId?: string }

/**
 * Liste des points qui empêchent un calcul FIABLE (ou tout calcul). « bloquant » : le serveur refusera (aucune pièce, échelle
 * non validée) ; « à saisir » : le calcul passe mais produit des points « à saisir » ; « à valider » : les entrées ne sont pas validées
 * (proposition calculée pour information, non reprenable).
 */
export function pointsManquants(plans: { id: string; nom: string; echelleValidee: boolean }[], pieces: PieceVue[], elements: ElementVue[]): Manque[] {
  const l: Manque[] = [];
  if (!pieces.length) l.push({ cle: 'aucune-piece', niveau: 'bloquant', message: 'Aucune pièce tracée : tracez au moins une pièce sur un plan.', planId: plans[0]?.id ?? null });
  for (const pl of plans) {
    if (!pl.echelleValidee && pieces.some((p) => p.planId === pl.id)) l.push({ cle: `echelle-${pl.id}`, niveau: 'bloquant', message: `${pl.nom} : échelle à valider (onglet Cheminements) — sans échelle validée, aucune proposition.`, planId: pl.id });
  }
  for (const p of pieces) {
    const manque = champsASaisir(p);
    if (manque.length) l.push({ cle: `piece-${p.id}`, niveau: 'a_saisir', message: `${p.nom} : ${manque.map((c) => LIBELLE_CHAMP[c]).join(', ')}.`, planId: p.planId, pieceId: p.id });
    else if (!p.validee) l.push({ cle: `validation-${p.id}`, niveau: 'a_valider', message: `${p.nom} : pièce à faire valider par le référent technique.`, planId: p.planId, pieceId: p.id });
  }
  if (pieces.length) {
    const types = new Set(elements.map((e) => e.type));
    if (!types.has('emplacement_ue') && !types.has('ue_imposee')) l.push({ cle: 'ue', niveau: 'a_saisir', message: 'Aucun emplacement autorisé pour le groupe extérieur : tracez-en au moins un.', planId: null });
    if (!types.has('evacuation_condensats')) l.push({ cle: 'condensats', niveau: 'a_saisir', message: 'Aucun point de rejet des condensats.', planId: null });
    if (!types.has('tableau_electrique')) l.push({ cle: 'tableau', niveau: 'a_saisir', message: 'Aucun tableau électrique : les alimentations ne seront pas tracées.', planId: null });
    const plansAvecPieces = new Set(pieces.map((p) => p.planId));
    if (plansAvecPieces.size > 1 && !elements.some((e) => e.type === 'passage_vertical' && e.attributs.lienId)) {
      l.push({ cle: 'passage', niveau: 'a_saisir', message: 'Plusieurs étages : tracez le passage entre étages sur chaque plan et reliez-les.', planId: null });
    }
    for (const e of elements.filter((x) => x.type === 'passage_vertical' && !x.attributs.lienId)) {
      l.push({ cle: `lien-${e.id}`, niveau: 'a_saisir', message: 'Passage entre étages non relié à celui de l’autre plan.', planId: e.planId, elementId: e.id });
    }
  }
  const ordre = { bloquant: 0, a_saisir: 1, a_valider: 2 };
  return l.sort((a, b) => ordre[a.niveau] - ordre[b.niveau]);
}

/** Corps PUT d'une pièce à partir de sa vue (tous les champs présents, comme l'exige le contrat). */
export function versSaisie(p: PieceVue, polygone: Pt[] = p.polygone): PieceSaisie {
  return {
    nom: p.nom, polygone, usage: p.usage, traitee: p.traitee, lieuId: p.lieuId, surfaceReleveeM2: p.surfaceReleveeM2, surfaceSource: p.surfaceSource,
    hauteurSousPlafondM: p.hauteurSousPlafondM, hauteurSource: p.hauteurSource, plafond: p.plafond, fauxPlafondACreer: p.fauxPlafondACreer, trame: p.trame,
    plenumMm: p.plenumMm, plenumSource: p.plenumSource, occupants: p.occupants, occupantsSource: p.occupantsSource, eclairageW: p.eclairageW, eclairageSource: p.eclairageSource,
    equipementsW: p.equipementsW, equipementsLatentW: p.equipementsLatentW, equipementsSource: p.equipementsSource, airNeufM3h: p.airNeufM3h, airNeufSource: p.airNeufSource,
    airNeufParCta: p.airNeufParCta, soufflageT: p.soufflageT, soufflageHr: p.soufflageHr, soufflageSource: p.soufflageSource, acoustiqueDba: p.acoustiqueDba,
    acoustiqueBase: p.acoustiqueBase, acoustiqueSource: p.acoustiqueSource, mursExterieurs: p.mursExterieurs.filter((i) => i < polygone.length),
  };
}
export const saisieVide = (nom: string, polygone: Pt[]): PieceSaisie => ({
  nom, polygone, usage: 'bureau', traitee: true, lieuId: null, surfaceReleveeM2: null, surfaceSource: null, hauteurSousPlafondM: null, hauteurSource: null,
  plafond: null, fauxPlafondACreer: false, trame: null, plenumMm: null, plenumSource: null, occupants: null, occupantsSource: null, eclairageW: null,
  eclairageSource: null, equipementsW: null, equipementsLatentW: null, equipementsSource: null, airNeufM3h: null, airNeufSource: null, airNeufParCta: false,
  soufflageT: null, soufflageHr: null, soufflageSource: null, acoustiqueDba: null, acoustiqueBase: null, acoustiqueSource: null, mursExterieurs: [],
});

/** Contrôles de la fiche avant envoi (le serveur refait les mêmes) : toute valeur saisie exige sa source. */
export function erreursSaisie(s: PieceSaisie): Record<string, string> {
  const e: Record<string, string> = {};
  const src = (v: unknown, source: string | null, cle: string) => { if (v != null && !(source ?? '').trim()) e[cle] = 'Source obligatoire pour une valeur saisie.'; };
  if (!s.nom.trim()) e.nom = 'Nom obligatoire.';
  src(s.surfaceReleveeM2, s.surfaceSource, 'surfaceSource'); src(s.hauteurSousPlafondM, s.hauteurSource, 'hauteurSource');
  src(s.plenumMm, s.plenumSource, 'plenumSource'); src(s.occupants, s.occupantsSource, 'occupantsSource'); src(s.eclairageW, s.eclairageSource, 'eclairageSource');
  src(s.equipementsW ?? s.equipementsLatentW, s.equipementsSource, 'equipementsSource'); src(s.airNeufM3h, s.airNeufSource, 'airNeufSource');
  src(s.soufflageT ?? s.soufflageHr, s.soufflageSource, 'soufflageSource'); src(s.acoustiqueDba, s.acoustiqueSource, 'acoustiqueSource');
  if (s.hauteurSousPlafondM != null && (s.hauteurSousPlafondM <= 0 || s.hauteurSousPlafondM > 50)) e.hauteur = 'Hauteur entre 0 et 50 m.';
  if (s.occupants != null && (!Number.isInteger(s.occupants) || s.occupants < 0)) e.occupants = 'Nombre entier positif.';
  const g = verifierPolygone(s.polygone);
  if (g) e.polygone = g;
  return e;
}

/* ─────────────────────────────── tracé ─────────────────────────────── */

/** Un clic près du premier sommet ferme le polygone (au moins 3 sommets) ; tolérance en unités du plan. */
export const fermeLePolygone = (points: Pt[], clic: Pt, tolerance: number) => points.length >= 3 && distance(points[0], clic) <= tolerance;
export const aireAffichee = (poly: Pt[], echelle: number | null) => (echelle && poly.length >= 3 ? aireM2(poly, echelle) : null);
/** Boîte englobante de plusieurs tracés (unités du plan) ; null si aucun point. */
export function emprise(traces: Pt[][]): { x0: number; y0: number; x1: number; y1: number } | null {
  const l = traces.flat();
  if (!l.length) return null;
  return { x0: Math.min(...l.map((p) => p[0])), y0: Math.min(...l.map((p) => p[1])), x1: Math.max(...l.map((p) => p[0])), y1: Math.max(...l.map((p) => p[1])) };
}

/* ─────────────────────────────── visuel : réseau ─────────────────────────────── */

/** Motif mis à l'échelle (u = une unité d'écran en unités du plan). */
export const motifEchelle = (motif: string, u: number) => (motif ? motif.split(' ').map((x) => Number(x) * u).join(' ') : undefined);

/* ─────────────────────────────── calcul, comparaison, reprise ─────────────────────────────── */

/** Message d'erreur clair d'un calcul refusé, selon le code du serveur. */
export function messageCalcul(code: string | undefined, message: string): { titre: string; texte: string; cheminements?: boolean } {
  switch (code) {
    case 'plan_trop_grand': return { titre: 'Plan trop grand pour le calcul', texte: `${message} Vérifiez l’échelle du plan et l’emprise des pièces tracées (au plus 250 m).` };
    case 'echelle_non_validee': return { titre: 'Échelle à valider', texte: message, cheminements: true };
    case 'donnees_invalides': return { titre: 'Calcul impossible', texte: message };
    case 'droit_insuffisant': return { titre: 'Action réservée', texte: 'Le calcul est réservé au référent technique, au chargé d’affaires et à l’administrateur.' };
    default: return { titre: 'Le calcul a échoué', texte: message };
  }
}

/** La variante peut-elle servir de base de travail ? (mêmes refus que le serveur, expliqués avant le clic). */
export function repriseImpossible(l: Pick<LigneVue, 'origine' | 'statut' | 'criteres'>, entreesValidees: boolean): string | null {
  if (l.origine === 'technicien') return 'La proposition du technicien est déjà sur le plan : rien à reprendre.';
  if (l.statut === 'rejetee' || l.statut === 'perimee') return `Variante ${LIBELLE_STATUT[l.statut].toLowerCase()} : relancez le calcul.`;
  if (!entreesValidees) return 'Entrées non validées (pièces, notice, réglages) : la proposition est calculée pour information et ne peut pas être reprise.';
  if (l.criteres.bloquants > 0) return `${l.criteres.bloquants} point(s) bloquant(s) ouvert(s) : la variante n’est pas recevable.`;
  return null;
}

